"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import Cookie from "js-cookie";
import { useState, useEffect } from "react";
import { DrawerV2, Text, MarkdownRenderer, Button } from "@nxt-costco-com/forge-components";
import { SpaceMd, SpaceSm } from "@nxt-costco-com/forge-design-tokens";
import { Analytics } from "../../../Analytics/index.js";
import { DeliveryMethodDrawer } from "../../DeliveryMethodDrawer/index.js";
import { CostcoBox } from "../../../Util/CostcoBox.js";
import { DeliveryAddressForm } from "./DeliveryAddressForm.js";
import { DeliveryLocationForm } from "./DeliveryLocationForm.js";
import { getDeliveryLocationDrawerDataProps, replaceStringTemplate } from "./lib.js";
import { DeliveryLocationDrawerPropsSchema, DeliveryLocationDrawerDataSchema } from "./types.js";
import { markdownStyles, primaryButtonStyles } from "./styles.js";
const COMPONENT_NAME = "DeliveryLocationDrawerUI";
const DeliveryLocationDrawerUI = (props) => {
  var _a, _b, _c, _d, _e, _f, _g;
  const {
    agent,
    zipCode,
    moduleSettings,
    isOpen,
    onSubmit,
    onClose,
    isBDPage,
    locale,
    warehouseSpecificDeliveryMethodUIOverrides,
    warehouseSpecificDeliveryMethodUIOverridesMap
  } = props;
  const hashedUserId = Cookie.get("hashedUserId");
  const [activePage, setActivePage] = useState(0);
  const [tmpLocationData, setTmpLocationData] = useState();
  const [isOnAddressFormPageRoute, setIsOnAddressFormPageRoute] = useState(false);
  useEffect(() => {
    if (activePage === 0) {
      setIsOnAddressFormPageRoute(false);
    }
  }, [activePage]);
  if (!moduleSettings || !moduleSettings[0]) {
    return null;
  }
  const useConfirmationPage = isBDPage && !!zipCode;
  let filteredModuleSettings = moduleSettings;
  if (moduleSettings.length > 1 && !useConfirmationPage) {
    filteredModuleSettings = filteredModuleSettings.filter(
      ({ development_key }) => development_key !== "delivery_location_confirmation"
    );
  }
  const useAddressFormPage = ((_a = tmpLocationData == null ? void 0 : tmpLocationData.errorConfig) == null ? void 0 : _a.type) === "enterFullDeliveryAddress" || isOnAddressFormPageRoute;
  if (!useAddressFormPage) {
    filteredModuleSettings = filteredModuleSettings.filter(
      ({ development_key }) => development_key !== "delivery_address"
    );
  } else if (!isOnAddressFormPageRoute) {
    setIsOnAddressFormPageRoute(true);
  }
  const useDeliveryMethodsPage = (_b = tmpLocationData == null ? void 0 : tmpLocationData.deliveryMethodsConfig) == null ? void 0 : _b.showDeliveryMethodPopup;
  if (moduleSettings.length > 1 && !useDeliveryMethodsPage) {
    filteredModuleSettings = filteredModuleSettings.filter(
      ({ development_key }) => development_key !== "delivery_methods"
    );
  }
  const data = getDeliveryLocationDrawerDataProps(filteredModuleSettings);
  if (!agent) {
    return null;
  }
  try {
    DeliveryLocationDrawerPropsSchema.parse(props);
    data.forEach((dataItem) => {
      DeliveryLocationDrawerDataSchema.parse(dataItem);
    });
  } catch (err) {
    console.error(err);
    return null;
  }
  const {
    backButtonAriaLabel,
    closeButtonAriaLabel,
    title,
    description,
    NDEnabledDescription,
    confirmationTemplate,
    helperText,
    buttonLabel,
    drawerSubtitle
  } = data[activePage] || {};
  const devKey = ((_c = filteredModuleSettings[activePage]) == null ? void 0 : _c.development_key) || "";
  let modalBodyDescription = ((_d = tmpLocationData == null ? void 0 : tmpLocationData.confirmationConfig) == null ? void 0 : _d.type) === "NDEnabledDrawerBody" ? NDEnabledDescription : description;
  if (confirmationTemplate && ((_e = tmpLocationData == null ? void 0 : tmpLocationData.confirmationConfig) == null ? void 0 : _e.type) === "confirmationTemplate" && ((_f = tmpLocationData == null ? void 0 : tmpLocationData.confirmationConfig) == null ? void 0 : _f.message)) {
    modalBodyDescription = replaceStringTemplate(confirmationTemplate, {
      message: (_g = tmpLocationData == null ? void 0 : tmpLocationData.confirmationConfig) == null ? void 0 : _g.message
    });
  }
  const handleClose = () => {
    onClose == null ? void 0 : onClose();
    setActivePage(0);
    setTmpLocationData(void 0);
    setIsOnAddressFormPageRoute(false);
  };
  const nextPage = (formValues) => {
    if (activePage + 1 < data.length) {
      setTmpLocationData(() => {
        setActivePage((page) => page + 1);
        return formValues;
      });
    } else {
      handleClose();
    }
  };
  const handleSubmit = async (formValues) => {
    const submitData = formValues || tmpLocationData;
    if (!!submitData) {
      if (activePage + 1 >= data.length) {
        const response = await (onSubmit == null ? void 0 : onSubmit(submitData));
        const { errorConfig } = response || {};
        if (errorConfig) {
          return {
            errorConfig
          };
        }
      }
      nextPage(submitData);
    } else {
      console.error(
        "An error occurred while attempting to update zip code. Undefined Data."
      );
    }
  };
  let warehouseSpecificDeliveryMethodUIOverridesToUse = warehouseSpecificDeliveryMethodUIOverrides;
  if (!warehouseSpecificDeliveryMethodUIOverridesToUse && (tmpLocationData == null ? void 0 : tmpLocationData.warehouse)) {
    warehouseSpecificDeliveryMethodUIOverridesToUse = warehouseSpecificDeliveryMethodUIOverridesMap == null ? void 0 : warehouseSpecificDeliveryMethodUIOverridesMap[tmpLocationData.warehouse];
  }
  return /* @__PURE__ */ jsx(
    Analytics,
    {
      analyticData: {
        component: {
          identifier: title == null ? void 0 : title.toLowerCase()
        }
      },
      children: /* @__PURE__ */ jsxs(
        DrawerV2,
        {
          "data-testId": "delivery-method-drawer",
          headerProps: {
            backButtonAriaLabel,
            closeButtonAriaLabel,
            level: activePage,
            onClose: handleClose,
            title,
            onBack: () => setActivePage((page) => page - 1)
          },
          open: isOpen,
          children: [
            drawerSubtitle && /* @__PURE__ */ jsx(Text, { variant: "t4", children: drawerSubtitle }),
            /* @__PURE__ */ jsxs(CostcoBox, { "data-testid": "deliverylocation", children: [
              modalBodyDescription && /* @__PURE__ */ jsx(
                MarkdownRenderer,
                {
                  uniqueId: "deliverylocation-description",
                  markdown: replaceStringTemplate(
                    modalBodyDescription,
                    {
                      oldZip: `${zipCode}` || "{{oldZip}}",
                      newZip: `${tmpLocationData == null ? void 0 : tmpLocationData.zipCode}` || "{{newZip}}"
                    }
                  ),
                  styles: {
                    ...markdownStyles,
                    p: { marginTop: "0" }
                  }
                }
              ),
              devKey === "delivery_location" && /* @__PURE__ */ jsx(
                DeliveryLocationForm,
                {
                  agent,
                  data: data[0],
                  onSubmit: useConfirmationPage || useAddressFormPage ? nextPage : handleSubmit,
                  onClose: handleClose,
                  skipValidation: isBDPage && !useConfirmationPage,
                  tmpZipCode: (tmpLocationData == null ? void 0 : tmpLocationData.zipCode) || "",
                  isBDPage,
                  locale
                }
              ),
              devKey === "delivery_address" && /* @__PURE__ */ jsx(
                DeliveryAddressForm,
                {
                  agent,
                  data: data[1],
                  onSubmit: useConfirmationPage ? nextPage : handleSubmit,
                  tmpZipCode: (tmpLocationData == null ? void 0 : tmpLocationData.zipCode) || "",
                  isBDPage,
                  locale
                }
              ),
              devKey === "delivery_location_confirmation" && /* @__PURE__ */ jsxs(
                CostcoBox,
                {
                  sx: {
                    display: "flex",
                    flexDirection: "column",
                    gap: SpaceSm,
                    marginTop: SpaceMd
                  },
                  children: [
                    /* @__PURE__ */ jsx(
                      Button,
                      {
                        sx: primaryButtonStyles,
                        "data-testid": "deliverylocationconfirmform--submit",
                        onClick: () => {
                          handleSubmit();
                        },
                        type: "submit",
                        children: buttonLabel
                      }
                    ),
                    /* @__PURE__ */ jsx(Button, { variant: "secondary", onClick: handleClose, children: "Cancel" })
                  ]
                }
              ),
              devKey === "delivery_methods" && /* @__PURE__ */ jsx(
                DeliveryMethodDrawer,
                {
                  agent,
                  onClose: handleClose,
                  onSubmit: !!tmpLocationData ? async (method) => {
                    return await handleSubmit({
                      warehouse: tmpLocationData.warehouse,
                      country: tmpLocationData.country,
                      city: tmpLocationData.city,
                      state: tmpLocationData.state,
                      zipCode: tmpLocationData.zipCode,
                      userSelectionDeliveryMethod: method
                    });
                  } : async (values) => {
                    return await handleSubmit(values);
                  },
                  moduleSettings: filteredModuleSettings[
                    activePage
                    // using unknown as we have two different type definitions for moduleSettings
                    // which is causing overlap issues
                  ],
                  deliveryMethodsConfig: tmpLocationData == null ? void 0 : tmpLocationData.deliveryMethodsConfig,
                  zipCode: `${zipCode}` || "",
                  locale,
                  warehouseSpecificDeliveryMethodUIOverrides: warehouseSpecificDeliveryMethodUIOverridesToUse
                }
              ),
              !hashedUserId && helperText && /* @__PURE__ */ jsx(
                MarkdownRenderer,
                {
                  uniqueId: "deliverylocation-helperText",
                  markdown: helperText,
                  styles: markdownStyles
                }
              )
            ] })
          ]
        }
      )
    }
  );
};
DeliveryLocationDrawerUI.displayName = COMPONENT_NAME;
export {
  DeliveryLocationDrawerUI
};
